import { useState, useCallback, useEffect } from 'react';

import { Document, Page, pdfjs } from 'react-pdf';
import classNames from 'classnames';
import { useResizeDetector } from 'react-resize-detector';

import { useLogger, usePdfWorkerSrc } from '../../services/hooks';
import { Icon } from '../../Icon';
import { useIsDesktop } from '../../hooks';
import { Box } from '../../Box';
import { CircularProgress } from '../../CircularProgress';
import { Button } from '../../Button';
import { ButtonGroup } from '../../ButtonGroup';

import { PreviewButtons } from './Buttons';
import { useStyles } from './Preview.styles';

/**
 * Свойства компонента {@link PreviewPdf}
 */
interface PreviewPdfProps {
  /**
   * URL файла PDF для предварительного просмотра.
   */
  url: string;

  /**
   * Форма предварительного просмотра ('auto' или 'square').
   */
  shape: 'auto' | 'square';

  /**
   * Функция, вызываемая при клике на кнопку печати.
   */
  onClickPrint?: () => void;

  /**
   * Функция, вызываемая при открытии предварительного просмотра.
   */
  onOpen?: () => void;

  /**
   * Асинхронная функция, вызываемая при клике на кнопку загрузки.
   */
  onClickDownload?: () => Promise<
    Blob | { file: Promise<Blob>; filename?: string }
  >;

  /**
   * Функция, вызываемая при успешной загрузке PDF.
   */
  onLoadSuccess?: () => void;

  /**
   * Флаг, указывающий, должен ли футер быть схлапываемым.
   */
  withCollapsableFooter?: boolean;

  /**
   * Функция, вызываемая для раскрытия превью.
   */
  onExpand?: () => void;
}

const options = {
  isEvalSupported: false,
};

const MIN_WIDTH_DISPLAY_BUTTONS = 174;

/**
 * Компонент для предварительного просмотра PDF файлов.
 *
 * @internal
 * @param props - Свойства компонента.
 * @returns Компонент предварительного просмотра PDF.
 */
export const PreviewPdf = ({
  url,
  shape,
  onClickPrint,
  onOpen,
  onClickDownload,
  onLoadSuccess,
  withCollapsableFooter,
  onExpand,
}: PreviewPdfProps) => {
  const { classes } = useStyles({});
  const isDesktop = useIsDesktop();
  const pdfWorkerSrc = usePdfWorkerSrc();

  if (pdfjs.GlobalWorkerOptions.workerSrc !== pdfWorkerSrc) {
    pdfjs.GlobalWorkerOptions.workerSrc = pdfWorkerSrc;
  }

  const [pagesCount, setPagesCount] = useState<number | null>(null);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [nextPageNumber, setNextPageNumber] = useState<number>(1); // Предотвращение скачков при загрузке страницы
  const [isLoaded, setIsLoaded] = useState(false);
  const logger = useLogger();
  const { width, ref } = useResizeDetector<HTMLDivElement>({
    refreshMode: 'debounce',
    refreshRate: 100,
  });

  useEffect(() => {
    setPageNumber(1);
    setNextPageNumber(1);
  }, [url]);

  const handleOpen = useCallback(() => onOpen && onOpen(), [onOpen]);

  const handleLoadSuccess = useCallback(
    ({ numPages }: { numPages: number }) => {
      setPagesCount(numPages);
      setIsLoaded(true);
      onLoadSuccess?.();
    },
    [setPagesCount, setIsLoaded, onLoadSuccess],
  );

  const handleSetPage = useCallback(
    () => setPageNumber(nextPageNumber),
    [nextPageNumber, setPageNumber],
  );

  const handlePrev = useCallback(
    (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
      event.preventDefault();
      event.stopPropagation();
      setNextPageNumber((x) => Math.max(1, x - 1));
    },
    [setNextPageNumber],
  );

  const handleNext = useCallback(
    (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
      event.preventDefault();
      event.stopPropagation();
      setNextPageNumber((x) => Math.min(pagesCount ?? 0, x + 1));
    },
    [setNextPageNumber, pagesCount],
  );

  const handleLoadError = (error: Error) => {
    logger.error(error, {
      tags: {
        vkdoc_error_type: 'pdf',
      },
    });
  };

  return (
    <div
      ref={ref}
      className={classNames(
        classes.root,
        classes.collapseWrapper,
        'aqa_file_preview_pdf',
      )}
    >
      {shape === 'square' && <div className={classes.square} />}
      <Box onClick={handleOpen}>
        <Document
          options={options}
          onLoadError={handleLoadError}
          onSourceError={handleLoadError}
          className={classNames(
            classes.document,
            shape === 'square' && classes.document_square,
          )}
          file={url}
          error={
            <Box p="32" color="text.light.secondary" textAlign="center">
              Ошибка при загрузке файла
            </Box>
          }
          loading={
            <Box display="flex" justifyContent="center" p="32">
              <CircularProgress size={50} />
            </Box>
          }
          onLoadSuccess={handleLoadSuccess}
        >
          {[
            <Page
              key={pageNumber}
              width={width === null ? 1 : width}
              pageNumber={pageNumber}
              renderAnnotationLayer
            />,
            nextPageNumber !== pageNumber && (
              <Page
                className={classes.hiddenPage}
                key={nextPageNumber}
                width={width === null ? 1 : width}
                pageNumber={nextPageNumber}
                onLoadSuccess={handleSetPage}
                renderAnnotationLayer
              />
            ),
          ]}
        </Document>
      </Box>
      <div className={classes.pagination}>
        {pagesCount !== null &&
          pagesCount > 1 &&
          (width ?? 0) > MIN_WIDTH_DISPLAY_BUTTONS && (
            <ButtonGroup
              variant="contained"
              size="small"
              onClick={(event) => event.stopPropagation()}
            >
              <Button
                disabled={nextPageNumber <= 1}
                onClick={handlePrev}
                icon={<Icon name="arrow_left_outline" size="16" />}
              />
              <Button disabled>
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  width={38}
                >
                  {pageNumber}/{pagesCount}
                </Box>
              </Button>
              <Button
                onClick={handleNext}
                icon={<Icon name="arrow_right_outline" size="16" />}
              />
            </ButtonGroup>
          )}
        {onExpand && isDesktop && (
          <Button
            icon={<Icon name="fullscreen_outline" size="20" />}
            size="small"
            onClick={onExpand}
          />
        )}
      </div>
      {(onClickDownload || onClickPrint) && isLoaded && (
        <PreviewButtons
          pdf
          onClickDownload={onClickDownload}
          withCollapsableFooter={withCollapsableFooter}
          onClickPrint={onClickPrint}
        />
      )}
    </div>
  );
};
